import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableActionsMenu, type TableAction } from "@/components/shared/TableActionsMenu";
import { Card } from "@/components/ui/card";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  getAuthorityLabelKey,
  getTaxLabelKey,
  formatPeriodForDisplay,
} from "@/lib/fiscalAgendaDisplay";
import { cn } from "@/lib/utils";
import type { TenantAccountantRequest } from "@/services/fiscalService";

type SolicitudesTab = "pendientes" | "completados";

type SolicitudMobileCardProps = {
  req: TenantAccountantRequest;
  tab: SolicitudesTab;
  inProgressRowActions: (req: TenantAccountantRequest) => TableAction[];
};

export function SolicitudMobileCard({
  req,
  tab,
  inProgressRowActions,
}: SolicitudMobileCardProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const ob = req.fiscalObligation;
  const inProgress = req.status === "in_progress";
  const completed = req.status === "completed";

  return (
    <Card
      className={cn(
        "overflow-hidden border-border p-4 text-left transition-colors duration-150",
        "hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      )}
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/requests/${req.id}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/requests/${req.id}`);
        }
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <p className="font-medium text-foreground">
            {t(getTaxLabelKey(ob.taxType))} ({t(getAuthorityLabelKey(ob.authority))}) –{" "}
            {formatPeriodForDisplay(ob.period, i18n.language)}
          </p>
          <p className="text-sm text-muted-foreground">
            {t("solicitudes.typePaymentSlip")}
          </p>
          <p className="text-sm text-muted-foreground">
            {formatDisplayDate(req.createdAt, { locale: i18n.language })}
          </p>
          <StatusBadge
            variant={completed ? "success" : inProgress ? "info" : "warning"}
          >
            {completed
              ? t("solicitudes.statusCompleted")
              : inProgress
                ? t("solicitudes.statusInProgress")
                : t("solicitudes.statusPending")}
          </StatusBadge>
          {tab === "completados" && (
            <p className="text-sm text-muted-foreground">
              {req.completedAt
                ? formatDisplayDate(req.completedAt, { locale: i18n.language })
                : "—"}
            </p>
          )}
        </div>
        <div
          className="shrink-0 text-muted-foreground"
          data-interactive
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {inProgress ? (
            <TableActionsMenu
              actions={inProgressRowActions(req)}
              onTriggerClick={(e) => e.stopPropagation()}
            />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden />
          )}
        </div>
      </div>
    </Card>
  );
}
